/**
 * Creators Query Hooks
 *
 * TanStack Query hooks for creator-related data fetching
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CreatorListFilters } from '../client/creators.client';
import { creatorsClient } from '../client/creators.client';
import { queryKeys } from '../query-keys';
import type { Creator } from '../types';

/**
 * Hook to fetch list of creators (requires auth)
 */
export function useCreatorsList(filters?: CreatorListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.creators.list(filters),
    queryFn: () => creatorsClient.list(filters),
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
}

/**
 * Hook to fetch public creators list (no auth required)
 */
export function useCreatorsListPublic(
  filters?: CreatorListFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.list(filters), 'public'],
    queryFn: () => creatorsClient.listPublic(filters),
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
}

/**
 * Hook to fetch creator profile
 * Stale time: 30 minutes (creator profile rarely changes)
 */
export function useCreatorProfile(id: string | number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.creators.detail(id!),
    queryFn: () => creatorsClient.getProfile(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Hook to fetch creator videos
 */
export function useCreatorVideos(
  id: string | number | null | undefined,
  params?: { sort_by?: string; per_page?: number; page?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.videos(id!), params],
    queryFn: () => creatorsClient.getVideos(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch creator shorts
 */
export function useCreatorShorts(
  id: string | number | null | undefined,
  params?: { sort_by?: string; page?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.shorts(id!), params],
    queryFn: () => creatorsClient.getShorts(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch creator series
 */
export function useCreatorSeries(
  id: string | number | null | undefined,
  params?: { sort_by?: string; page?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.series(id!), params],
    queryFn: () => creatorsClient.getSeries(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch creator posts
 */
export function useCreatorPosts(
  id: string | number | null | undefined,
  params?: { sort_by?: string; page?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.posts(id!), params],
    queryFn: () => creatorsClient.getPosts(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch creator courses
 */
export function useCreatorCourses(
  id: string | number | null | undefined,
  params?: { sort_by?: string; page?: number }
) {
  return useQuery({
    queryKey: [...queryKeys.creators.courses(id!), params],
    queryFn: () => creatorsClient.getCourses(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to fetch creator by handler
 */
export function useCreatorByHandler(
  handler: string,
  options?: { staleTime?: number; refetchOnWindowFocus?: boolean }
) {
  return useQuery<{ creators: Creator[] }>({
    queryKey: [...queryKeys.creators.list({ handler }), 'by-handler'],
    queryFn: async () => {
      const response = await creatorsClient.listPublic({ handler });
      return { creators: response.data || response.creators || [] };
    },
    enabled: !!handler,
    staleTime: options?.staleTime ?? 1000 * 60 * 5, // 5 minutes default
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
}

// ============================================================================
// Infinite Query Hooks for Creator Content Tabs
// ============================================================================

/**
 * Hook to fetch creator videos with infinite scroll
 */
export function useCreatorVideosInfinite(
  creatorId: string | number | null | undefined,
  filters?: { sort_by?: string }
) {
  const resolvedFilters = { sort_by: 'newest', per_page: 15, ...filters };
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.videos(creatorId!), 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const params = pageParam ? { page: pageParam, ...resolvedFilters } : resolvedFilters;
      return creatorsClient.getVideos(creatorId!, params);
    },
    getNextPageParam: (lastPage) =>
      lastPage.next_page_url ? lastPage.current_page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
    enabled: !!creatorId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook to fetch creator shorts with infinite scroll
 */
export function useCreatorShortsInfinite(
  creatorId: string | number | null | undefined,
  filters?: { sort_by?: string }
) {
  const resolvedFilters = { sort_by: 'newest', per_page: 21, ...filters };
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.shorts(creatorId!), 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const params = pageParam ? { page: pageParam, ...resolvedFilters } : resolvedFilters;
      return creatorsClient.getShorts(creatorId!, params);
    },
    getNextPageParam: (lastPage) =>
      lastPage.next_page_url ? lastPage.current_page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
    enabled: !!creatorId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook to fetch creator series with infinite scroll
 */
export function useCreatorSeriesInfinite(
  creatorId: string | number | null | undefined,
  filters?: { sort_by?: string }
) {
  const resolvedFilters = { sort_by: 'newest', per_page: 21, ...filters };
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.series(creatorId!), 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const params = pageParam ? { page: pageParam, ...resolvedFilters } : resolvedFilters;
      return creatorsClient.getSeries(creatorId!, params);
    },
    getNextPageParam: (lastPage) =>
      lastPage.next_page_url ? lastPage.current_page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
    enabled: !!creatorId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook to fetch creator posts with infinite scroll
 */
export function useCreatorPostsInfinite(
  creatorId: string | number | null | undefined,
  filters?: { sort_by?: string }
) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.posts(creatorId!), 'infinite', filters],
    queryFn: async ({ pageParam }) => {
      const params = pageParam ? { page: pageParam, ...filters } : filters;
      return creatorsClient.getPosts(creatorId!, params);
    },
    getNextPageParam: (lastPage) =>
      lastPage.next_page_url ? lastPage.current_page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
    enabled: !!creatorId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook to fetch creator courses (Education tab) with infinite scroll
 * Note: Education API uses 'old' (not 'oldest') for descending sort
 */
export function useCreatorCoursesInfinite(
  creatorId: string | number | null | undefined,
  filters?: { sort_by?: string }
) {
  const resolvedFilters = { sort_by: 'newest', per_page: 21, ...filters };
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.courses(creatorId!), 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const params = pageParam ? { page: pageParam, ...resolvedFilters } : resolvedFilters;
      return creatorsClient.getCourses(creatorId!, params);
    },
    getNextPageParam: (lastPage) =>
      lastPage.next_page_url ? lastPage.current_page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
    enabled: !!creatorId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

// ============================================================================
// Creator Profile Tabs (consumer, subscription-gated)
// ============================================================================

/**
 * Active profile tabs for a channel. Subscription-gated — only enabled for
 * subscribers (guests/unpublished channels return 404, surfaced as empty).
 */
export function useCreatorTabs(
  creatorId: string | number | null | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && !!creatorId;
  return useQuery({
    queryKey: queryKeys.creators.tabs(creatorId!),
    queryFn: () => creatorsClient.getTabs(creatorId!),
    enabled,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * A single tab's videos with infinite scroll. The consumer detail endpoint
 * paginates as { current_page, per_page, total } with no next_page_url, so the
 * next page is derived from total / per_page.
 */
export function useCreatorTabInfinite(
  creatorId: string | number | null | undefined,
  tabUuid: string | null | undefined,
  options?: { perPage?: number }
) {
  const perPage = options?.perPage ?? 20;
  return useInfiniteQuery({
    queryKey: [...queryKeys.creators.tab(creatorId!, tabUuid!), 'infinite', perPage],
    queryFn: async ({ pageParam }) =>
      creatorsClient.getTab(creatorId!, tabUuid!, { page: pageParam, per_page: perPage }),
    getNextPageParam: (lastPage) => {
      const { current_page, per_page, total } = lastPage.videos;
      const lastPageNum = Math.max(1, Math.ceil(total / per_page));
      return current_page < lastPageNum ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!creatorId && !!tabUuid,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}
